import { auth, jwApi } from '@jwplayer/companion-gateway';
import { ApiCache } from '@jwplayer/fe-common/module/api-cache';
import { WuiListItemHeader, WuiSelectInput } from '@jwplayer/wui-react';
import css from './style.module.scss';

const SITES_CACHE_LENGTH = 100;

export const sitesNameCache = new ApiCache(
  {
    list: async (params) => {
      // Empty search should give us first (up to) x items
      const results = (
        await jwApi('sites', 'search', {
          input: {
            site_name: '',
          },
          queryParams: {
            page_length: params.pageLength,
          },
        })
      ).data;

      return {
        // Use number of sites returned by dashboard_scope
        total: auth.getCache()?.sites.length ?? results.length,
        results: results.map((r) => {
          return {
            id: r.site_id,
            name: r.name,
          };
        }),
      };
    },
    get: async (id) => {
      const site = (await jwApi('sites', 'read', { routeParams: { siteId: id } })).data;

      return {
        id: site.id,
        name: site.metadata.name,
      };
    },
    search: async (phrase) => {
      if (!phrase) {
        // Don't submit empty search phrase
        return;
      }

      const results = (
        await jwApi('sites', 'search', {
          input: {
            site_name: phrase,
          },
          queryParams: {},
        })
      ).data;

      return results.map((r) => {
        return {
          id: r.site_id,
          name: r.name,
        };
      });
    },
  },
  {
    maxEntries: SITES_CACHE_LENGTH,
    pageLength: SITES_CACHE_LENGTH,
  },
);

/**
 * ⚠ Will not display if user has only 1 site available to choose from ⚠
 */
export function SiteSelectInput<AllSitesOption extends boolean = false>({
  value,
  onValueChange,
  addAllSitesOption,
  addRecentSiteOption,
  inputButtonOptions,
  hasSimpleLabel,
  disabled,
  'data-test': dataTest,
  ...props
}: {
  value?: AllSitesOption extends true ? null | string : string;
  onValueChange?: (value: AllSitesOption extends true ? null | string : string) => void;
  addAllSitesOption?: boolean | undefined;
  addRecentSiteOption?: boolean | undefined;
  inputButtonOptions?: {
    type?: 'plain' | 'rich';
    size?: 'md' | 'lg';
  };
  hasSimpleLabel?: boolean | undefined;
  disabled?: boolean;
  'data-test': string;
} & React.ComponentProps<'div'>) {
  const handleInput = (e: React.FormEvent<HTMLWuiSelectInputElement>) => {
    const siteId = (e.target as HTMLWuiSelectInputElement).value;

    if (siteId && onValueChange) {
      // TS does not understand this
      onValueChange((siteId === 'NULL' ? null : siteId) as never);
    }
  };

  if (sitesNameCache.getEntries().length === 1) {
    // Hide when there are no properties to choose from
    return null;
  }

  return (
    <>
      {!hasSimpleLabel && <WuiListItemHeader label="Property" />}
      {hasSimpleLabel && <div className={css.label}>Property</div>}
      <WuiSelectInput
        searchable
        data-test={dataTest}
        placeholder="Select a property"
        inputButtonOptions={{
          size: inputButtonOptions?.size ?? 'lg',
          type: inputButtonOptions?.type ?? 'rich',
        }}
        options={async (query) => {
          const arr = query ? await sitesNameCache.search(query) : sitesNameCache.getEntries();

          const options: {
            label: string;
            value: string;
          }[] = (arr ?? []).map((site) => {
            return {
              label: site.name,
              value: site.id,
            };
          });

          if (addAllSitesOption) {
            options.unshift({
              label: 'All properties',
              value: 'NULL',
            });
          }

          if (addRecentSiteOption) {
            options.unshift({
              label: 'Most recently used',
              value: 'recent',
            });
          }

          return options;
        }}
        extraOptions={({ noSearchResults }) => {
          if (sitesNameCache.total <= SITES_CACHE_LENGTH) {
            return;
          }

          if (noSearchResults) {
            return;
          }

          return {
            footer: [
              { type: 'divider' },
              {
                type: 'meta',
                label: 'Type in the search bar to see the rest of your properties',
              },
            ],
          };
        }}
        resolveInitialValue={async function () {
          if (!this.value || this.value === 'NULL' || this.value === 'recent') {
            return;
          }

          const name = (await sitesNameCache.get(this.value))?.name;
          if (!name) {
            return;
          }

          return {
            label: name,
            value: this.value,
          };
        }}
        onInput={handleInput}
        value={value === null ? 'NULL' : (value as string | undefined)}
        className={props.className}
        disabled={disabled}
      />
    </>
  );
}
